var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _FsChoiceButton_instances, _FsChoiceButton_connectedController, _FsChoiceButton_dispatchPrivateChangeEvent, _FsChoiceButton_onBrowserInvalid, _FsChoiceButton_onChange, _FsChoiceButton_onHostChange, _FsChoiceButton_onKeydown;
import 'element-internals-polyfill';
import { canonicalName, fsChoiceButtonDefaults } from '@cof/fs-choice-button-core';
import choiceButtonStyles from '@cof/fs-choice-button-core/lib/choice-button.lit.js';
import { FormControlMixin } from '@open-wc/form-control';
import { submit } from '@open-wc/form-helpers';
import { css, html, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { choiceButtonRequiredValidator } from './choiceButtonRequiredValidator.js';
import { PrivateChangeEvent, privateChangeEvent } from './privateChangeEvent.js';
/**
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-choice-button-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `fs-choice-button` call
 *
 * ```javascript
 * import '@cof/fs-choice-button-wc/fs-choice-button.js';
 * ```
 *
 * To import the `ChoiceButton` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { ChoiceButton } from '@cof/fs-choice-button-wc';
 * ```
 * @element fs-choice-button
 * @slot choice-button-icon - Takes a single icon to render with the choice button
 */
export class FsChoiceButton extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        _FsChoiceButton_instances.add(this);
        /**
         * The alignment property controls the choice button's inner alignment of icon and label/subtext
         * @attr alignment
         */
        this.alignment = fsChoiceButtonDefaults.alignment;
        /**
         * Set compact to `true` when using the element in high-density user interfaces.
         * This property will decrease the overall size of an individual choice button
         * and should generally be used in conjunction with the compact property of the
         * choice button group which decreases the spacing between individual buttons.
         * Use sparingly.
         */
        this.compact = fsChoiceButtonDefaults.compact;
        /**
         * When disabled is true, the element will become non-interactable and should be treated as if not present on the page
         * @attr disabled
         */
        this.disabled = false;
        /**
         * The text of the internal choice button's label used for accessibility purposes
         * @attr label-text
         */
        this.labelText = '';
        /**
         * The form control name
         * @attr name
         */
        this.name = undefined;
        /**
         * When true the input will be set to a required state. For inputs of type checkbox,
         * this means that the individual control will be required to be selected. For inputs
         * of type 'radio', one item in the group must be selected.
         * @attr required
         */
        this.required = false;
        /**
         * Indicates the type of choice button to be rendered. When the component is set
         * to 'radio', the choice button behaves the same way an input[type="radio"]
         * element would: single choice, focus toggled by arrow keys, etc. When the type
         * is set to 'checkbox', the choice button behaves as a checkbox control: multi-
         * options, every component is tabbable, etc.
         * @attr type
         */
        this.type = fsChoiceButtonDefaults.type;
        /**
         * When selected is true, the choice button will be in a selected state indicating a user choice
         * @attr selected
         */
        this.selected = fsChoiceButtonDefaults.selected;
        /**
         * The choice button's value for participating in a parent form
         * @attr value
         */
        this.value = undefined;
        /**
         * Used to override the default required error message. Note: You should make sure each
         * item in a given choice button group uses the same valueMissingMessage; otherwise, you might
         * get inconsistent results depending on which item in the group validates first.
         *
         * @attr value-missing-message
         */
        this.valueMissingMessage = '';
        /** Determines if the control is focusable */
        this.isFocusable = true;
        /** Use an abort controller to inform listeners when to disconnect */
        _FsChoiceButton_connectedController.set(this, void 0);
        /** Catch default browser invalid events and cancel the popup behavior */
        _FsChoiceButton_onBrowserInvalid.set(this, (event) => {
            if (event.isTrusted) {
                event.preventDefault();
                this.focus();
            }
        });
        /**
         * Method to call when a private change event occurs on the host's root node
         * @private
         */
        _FsChoiceButton_onHostChange.set(this, (event) => {
            if (this.type === 'radio') {
                const target = event.checkedChoiceButton;
                if (target !== this && target.name === this.name && this.selected) {
                    this._reset();
                }
                this.isFocusable = this.selected;
            }
            /** Guarantee that validators run for each item in the group to keep them in sync */
            if (event.checkedChoiceButton !== this) {
                this.setValue(this.selected ? this.value : '');
            }
        });
    }
    render() {
        return html `
    <input
      class="fs-choice-button__control"
      id="choice-button"
      name="${this.name}"
      tabindex="${this.canReceiveFocus}"
      .checked="${this.selected}"
      ?disabled="${this.disabled}"
      .type="${this.type}"
      ?required="${this.required}"
      .value="${this.value}"
      @change="${__classPrivateFieldGet(this, _FsChoiceButton_instances, "m", _FsChoiceButton_onChange)}"
      @keydown="${__classPrivateFieldGet(this, _FsChoiceButton_instances, "m", _FsChoiceButton_onKeydown)}"
    >
    <label
      class="fs-choice-button fs-choice-button--alignment-${this.alignment} ${this.compact ? 'fs-choice-button--compact' : ''}"
      for="choice-button"
    >
      ${this.selected
            ? html `<slot name="choice-button-icon-selected">
        <slot name="choice-button-icon"></slot>
      </slot>`
            : html `<slot name="choice-button-icon"></slot>`}
      <div class="fs-choice-button__text">
        <span class="fs-choice-button__label">
          ${this.labelText}
        </span>
        ${this.subtext
            ? html `
          <span class="fs-choice-button__subtext">
            ${this.subtext}
          </span>
        `
            : nothing}
      </div>
    </label>`;
    }
    /**
     * Attach root node event listeners for private events on connected
     * this allows the element to be disconnected and reconnected as well as
     * adopted into new DocumentOrShadowRoots.
     */
    connectedCallback() {
        super.connectedCallback();
        /** Attach a connected controller for the current DOM connection */
        __classPrivateFieldSet(this, _FsChoiceButton_connectedController, new AbortController(), "f");
        /** Listen for privateChangeEvents but only on the root node to avoid conflicts */
        this.getRootNode().addEventListener(privateChangeEvent, __classPrivateFieldGet(this, _FsChoiceButton_onHostChange, "f"), {
            signal: __classPrivateFieldGet(this, _FsChoiceButton_connectedController, "f").signal
        });
        /** Catch default invalid behavior */
        this.addEventListener('invalid', __classPrivateFieldGet(this, _FsChoiceButton_onBrowserInvalid, "f"), {
            signal: __classPrivateFieldGet(this, _FsChoiceButton_connectedController, "f").signal
        });
    }
    /** Remove event listeners added in the connectedCallback */
    disconnectedCallback() {
        super.disconnectedCallback();
        __classPrivateFieldGet(this, _FsChoiceButton_connectedController, "f").abort();
    }
    /** Forward the click method to the internal input */
    click() {
        this.validationTarget.click();
    }
    /** Determines if the element can receive focus based on the type and current selection */
    get canReceiveFocus() {
        if (this.type === 'radio') {
            if (this.selected) {
                return 0;
            }
            return this.isFocusable ? 0 : -1;
        }
        return 0;
    }
    /** Lit lifecycle method for the first update */
    firstUpdated() {
        /** Ensure the host's role is set appropriately */
        this.internals.role = this.type;
    }
    /**
     * Lifecycle method for the FormControlMixin, like a default
     * checkbox or radio button, the default state
     * is if the element itself has the checked attribute
     * @private
     */
    resetFormControl() {
        this.selected = this.hasAttribute('selected');
    }
    /**
     * Lifecycle method from the FormControlMixin, when this method returns false
     * the element's value will be removed from the parent form
     * @private
     */
    shouldFormValueUpdate() {
        return this.selected;
    }
    /**
     * Allows the developer to specificy a new validition message for the required
     * validatior by setting the valueMissingMessage on the control
     * @private
     */
    validityCallback(validationKey) {
        if (validationKey === 'valueMissing' && this.valueMissingMessage) {
            return this.valueMissingMessage;
        }
    }
    /** Lit lifecycle method that is run when a reactive property changes */
    updated(changed) {
        /** Update the form value any time the selected or value properties change */
        if (changed.has('selected') || changed.has('value')) {
            this.setValue(this.value);
        }
        /** Toggle the element state when the control becomes selected */
        if (changed.has('selected')) {
            this.internals.states[this.selected ? 'add' : 'delete']('--selected');
        }
        /** Set the host's role based on the choice button type */
        if (changed.has('type')) {
            this.internals.role = this.type;
        }
    }
    /**
     * Reset the internals instance for this named group and remove checked if present
     * @private
     */
    _reset() {
        this.selected = false;
        this.setValue(null);
    }
}
_FsChoiceButton_connectedController = new WeakMap(), _FsChoiceButton_onBrowserInvalid = new WeakMap(), _FsChoiceButton_onHostChange = new WeakMap(), _FsChoiceButton_instances = new WeakSet(), _FsChoiceButton_dispatchPrivateChangeEvent = function _FsChoiceButton_dispatchPrivateChangeEvent() {
    const privateChangeEvent = new PrivateChangeEvent(this);
    this.getRootNode().dispatchEvent(privateChangeEvent);
}, _FsChoiceButton_onChange = function _FsChoiceButton_onChange(event) {
    this.selected = event.target.checked;
    this.setValue(this.value);
    __classPrivateFieldGet(this, _FsChoiceButton_instances, "m", _FsChoiceButton_dispatchPrivateChangeEvent).call(this);
    this.dispatchEvent(new Event(event.type, event));
}, _FsChoiceButton_onKeydown = function _FsChoiceButton_onKeydown(event) {
    if (event.code === 'Enter') {
        submit(this.form);
    }
    if (this.type === 'radio') {
        /** Only worry about arrow keys */
        if (!['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'].includes(event.code)) {
            return;
        }
        const hostRoot = this.getRootNode();
        const options = hostRoot.querySelectorAll(`${this.localName}[name="${this.name}"]`);
        if (options.length > 1) {
            const index = Array.from(options).indexOf(this);
            const code = event.code;
            let next;
            if (code === 'ArrowDown' || code === 'ArrowRight') {
                next = options[index + 1] ? options[index + 1] : options[0];
            }
            else if (code === 'ArrowUp' || code === 'ArrowLeft') {
                next = options[index - 1] ? options[index - 1] : options[options.length - 1];
            }
            this._reset();
            next?.focus();
            next?.click();
        }
    }
};
FsChoiceButton.canonicalName = canonicalName;
FsChoiceButton.styles = [
    choiceButtonStyles,
    css `
    :host {
      display: flex;
      flex: 1 0 0;
      width: 0;
    }
  `
];
FsChoiceButton.formControlValidators = [choiceButtonRequiredValidator];
/** Delegate focus to the internal input by default */
FsChoiceButton.shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true
};
__decorate([
    property()
], FsChoiceButton.prototype, "alignment", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsChoiceButton.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsChoiceButton.prototype, "disabled", void 0);
__decorate([
    property({ type: String, attribute: 'label-text' })
], FsChoiceButton.prototype, "labelText", void 0);
__decorate([
    property()
], FsChoiceButton.prototype, "name", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsChoiceButton.prototype, "required", void 0);
__decorate([
    property()
], FsChoiceButton.prototype, "type", void 0);
__decorate([
    property({ type: Boolean, reflect: false })
], FsChoiceButton.prototype, "selected", void 0);
__decorate([
    property()
], FsChoiceButton.prototype, "subtext", void 0);
__decorate([
    property()
], FsChoiceButton.prototype, "value", void 0);
__decorate([
    property({ attribute: 'value-missing-message' })
], FsChoiceButton.prototype, "valueMissingMessage", void 0);
__decorate([
    query('input')
], FsChoiceButton.prototype, "validationTarget", void 0);
__decorate([
    state()
], FsChoiceButton.prototype, "isFocusable", void 0);
//# sourceMappingURL=ChoiceButton.js.map